import { makeAutoObservable, runInAction } from 'mobx';

import { fromBaseProductType } from '@/collection/entity/CollectionType';
import { filterTreeDeep } from '@/common/components/SortableTree/SortableTree.utils';
import { flattenTree } from '@/common/components/Tree/TreeItemView/TreeItemView.utils';
import { collectSubtreeIds, findItemDeep } from '@/common/helpers/tree.utils';
import useStore from '@/common/hooks/useStore';
import Notifier from '@/common/services/Notifier';
import { createDriveRootSection } from '@/drive/constants/sidebarTreeSections';
import { DriveItemType } from '@/drive/entities/DriveItem/DriveItem';
import { DriveItemId } from '@/drive/entities/DriveItemId';
import { CollectionTreeItem } from '@/drive/entities/DriveTreeItem/CollectionTreeItem';
import { DriveTreeItem, fromDriveNode } from '@/drive/entities/DriveTreeItem/DriveTreeItem';
import { DriveModifierStore } from '@/drive/stores/DriveModifierStore/DriveModifierStore';
import DriveTreeStore from '@/drive/stores/DriveTreeStore';
import { DriveSection } from '@/drive/types/DriveSection';
import { createEmptyForm } from '@/forwarding/types/ForwardForm';
import { BaseProductType } from '@/product/constants/productTypes';

import {
  createLocalCollectionItem,
  createLocalFolderItem,
  getCollectionDisabledReason,
  prepareModalTreeItems,
} from './DriveModalTreeStore.utils';

class DriveModalTreeStore {
  filter: string = '';

  private expandedIds = new Set<DriveTreeItem['id']>([DriveSection.DriveRoot]);

  private forwardedItemId: Nullable<DriveItemId> = null;

  get tree(): Nullable<DriveTreeItem[]> {
    if (!this.driveTreeStore.tree) {
      return null;
    }

    const driveRoot = createDriveRootSection();

    driveRoot.children = this.driveTreeStore.tree.map(fromDriveNode);

    const tree = prepareModalTreeItems([driveRoot], this.expandedIds);

    if (this.newItem && this.newItemParentId !== null) {
      const parent = findItemDeep(tree, this.newItemParentId);

      if (parent) {
        parent.children = [this.newItem, ...(parent.children ?? [])];
      }
    }

    return tree;
  }

  get filteredTree(): Nullable<DriveTreeItem[]> {
    if (!this.tree) {
      return null;
    }

    const filterText = this.filter.toLowerCase().trim();

    if (filterText.length === 0) {
      return this.tree;
    }

    return filterTreeDeep(this.tree, (item) => item.title.toLowerCase().includes(filterText));
  }

  get disabledReasons(): Map<DriveTreeItem['id'], string> {
    if (!this.tree) {
      return new Map();
    }

    const forwardedSubtreeIds = this.forwardedItemId ? collectSubtreeIds(this.tree, this.forwardedItemId) : [];

    return new Map([
      ...this.getDisabledCollections().map(
        (collection): [DriveTreeItem['id'], string] => [collection.id, getCollectionDisabledReason(collection, this.productType, this.itemsCount)],
      ),
      ...forwardedSubtreeIds.map((id): [DriveTreeItem['id'], string] => [id, 'An item can’t be placed inside itself']),
    ]);
  }

  selectedId: Nullable<DriveTreeItem['id']> = null;

  get selected(): Nullable<DriveTreeItem> {
    if (this.tree && this.selectedId) {
      return findItemDeep(this.tree, this.selectedId) ?? null;
    } else {
      return null;
    }
  }

  newItemParentId: Nullable<DriveTreeItem['id']> = null;

  newItem: Nullable<DriveTreeItem> = null;

  isPendingCreation = false;

  private creationPromise: Nullable<Promise<void>> = null;

  productType: Nullable<BaseProductType> = null;

  private itemsCount = 1;

  constructor(
    private driveModifierStore: DriveModifierStore,
    private driveTreeStore: DriveTreeStore,
  ) {
    makeAutoObservable<DriveModalTreeStore, 'forwardedItemId' | 'itemsCount' | 'creationPromise' | 'createTreeItem'>(this, {
      forwardedItemId: false,
      creationPromise: false,
      productType: false,
      itemsCount: false,
      createTreeItem: false,
    });
  }

  refresh = async (
    productType: Nullable<BaseProductType>,
    forwardedItemId: Nullable<DriveItemId>,
    itemsCount = 1,
  ) => {
    this.productType = productType;
    this.itemsCount = itemsCount;
    this.forwardedItemId = forwardedItemId;

    await this.driveTreeStore.refresh();
  };

  reset = () => {
    this.filter = '';
    this.expandedIds = new Set([DriveSection.DriveRoot]);
    this.selectedId = null;
    this.newItemParentId = null;
    this.newItem = null;
    this.isPendingCreation = false;
    this.creationPromise = null;
    this.productType = null;
    this.itemsCount = 1;
    this.forwardedItemId = null;
  };

  toggleCollapse = (item: DriveTreeItem) => {
    if (this.expandedIds.has(item.id)) {
      this.expandedIds.delete(item.id);
    } else {
      this.expandedIds.add(item.id);
    }
  };

  setSelectedId = (id: DriveItemId) => {
    this.selectedId = id;
  };

  changeFilter = (filter: string) => {
    this.filter = filter;
  };

  editTitle = (title: string) => {
    if (!this.newItem || this.isPendingCreation) {
      return;
    }

    this.newItem.setTitle(title);
  };

  startCreation = async (groupType: DriveItemType.Folder | DriveItemType.Collection) => {
    if (this.isPendingCreation) {
      return;
    }

    if (this.selected instanceof CollectionTreeItem) {
      Notifier.warn('Please select folder');

      return;
    }

    if (this.newItem) {
      await this.finishCreation();
    }

    if (!this.tree) {
      return;
    }

    const newItemParentId = this.selectedId ?? DriveSection.DriveRoot;
    const { productType } = this;

    if (groupType === DriveItemType.Collection && !productType) {
      throw new Error('startCreation called when productType is null');
    }

    const newItem =
      groupType === DriveItemType.Collection && productType
        ? createLocalCollectionItem(fromBaseProductType(productType))
        : createLocalFolderItem();

    runInAction(() => {
      this.newItemParentId = newItemParentId;
      this.expandedIds.add(newItemParentId);
      this.newItem = newItem;
      this.selectedId = newItem.id;
    });
  };

  finishCreation = async () => {
    if (this.creationPromise) {
      return this.creationPromise;
    }

    if (!this.tree || !this.newItem) {
      throw new Error('finishCreation called when tree or newItem is null');
    }

    this.isPendingCreation = true;

    try {
      this.creationPromise = this.createTreeItem();
      await this.creationPromise;
    } finally {
      runInAction(() => {
        this.isPendingCreation = false;
        this.creationPromise = null;
      });
    }
  };

  private createTreeItem = async () => {
    const parentId = this.newItemParentId!;
    const local = this.newItem!;
    const form = { ...createEmptyForm(), title: local.title };
    const normalizedParentId = parentId === DriveSection.DriveRoot ? null : (parentId as number);

    if (local instanceof CollectionTreeItem && !this.productType) {
      throw new Error('finishCreation called for collection when productType is null');
    }

    const res =
      local instanceof CollectionTreeItem
        ? await this.driveModifierStore.createCollection(form, this.productType!, normalizedParentId)
        : await this.driveModifierStore.createFolder(form, normalizedParentId);

    if (res.hasErrors()) {
      res.getErrors().forEach((error) => Notifier.error(error));

      return;
    }

    const remoteItem = res.getItems()[0]!;

    runInAction(() => {
      if (this.selectedId === local.id) {
        this.selectedId = remoteItem.id;
      }

      this.newItem = null;
      this.newItemParentId = null;
    });
  };

  private getDisabledCollections(): CollectionTreeItem[] {
    if (!this.tree) {
      return [];
    }

    const collections = flattenTree(this.tree)
      .map((item) => item.original)
      .filter((item): item is CollectionTreeItem => item instanceof CollectionTreeItem);

    return this.productType
      ? collections.filter((collection) => collection.collectionType !== fromBaseProductType(this.productType!))
      : collections;
  }
}

export const useDriveModalTreeStore = () => useStore(DriveModalTreeStore);

export default DriveModalTreeStore;
